/**
 * Condition Builder - Visual interface for creating conditional formatting rules
 * 
 * Replaces free-text condition input with intuitive dropdowns
 */

import { useState } from 'react';
import './ConditionBuilder.css';

const OPERATORS = [
    { value: 'equals', label: 'equals', symbol: '==' },
    { value: 'not_equals', label: 'does not equal', symbol: '!=' },
    { value: 'greater_than', label: 'is greater than', symbol: '>' },
    { value: 'greater_than_or_equal', label: 'is greater than or equal to', symbol: '>=' },
    { value: 'less_than', label: 'is less than', symbol: '<' },
    { value: 'less_than_or_equal', label: 'is less than or equal to', symbol: '<=' },
    { value: 'contains', label: 'contains', symbol: 'includes' },
    { value: 'not_contains', label: 'does not contain', symbol: '!includes' },
    { value: 'starts_with', label: 'starts with', symbol: 'startsWith' },
    { value: 'ends_with', label: 'ends with', symbol: 'endsWith' },
    { value: 'is_empty', label: 'is empty', symbol: 'empty' },
    { value: 'is_not_empty', label: 'is not empty', symbol: '!empty' }
];

const VALUE_TYPES = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' }
];

/**
 * Parse a condition string back into structured format
 */
const parseCondition = (conditionStr) => {
    if (!conditionStr) {
        return { column: 'value', operator: 'greater_than', compareValue: '', valueType: 'number' };
    }

    // Try to parse common patterns
    const patterns = [
        { regex: /(\w+)\s*>\s*(.+)/, operator: 'greater_than' },
        { regex: /(\w+)\s*>=\s*(.+)/, operator: 'greater_than_or_equal' },
        { regex: /(\w+)\s*<\s*(.+)/, operator: 'less_than' },
        { regex: /(\w+)\s*<=\s*(.+)/, operator: 'less_than_or_equal' },
        { regex: /(\w+)\s*===?\s*['"](.+)['"]/, operator: 'equals' },
        { regex: /(\w+)\s*===?\s*(.+)/, operator: 'equals' },
        { regex: /(\w+)\s*!==?\s*['"](.+)['"]/, operator: 'not_equals' },
        { regex: /(\w+)\s*!==?\s*(.+)/, operator: 'not_equals' }
    ];

    for (const pattern of patterns) {
        const match = conditionStr.match(pattern.regex);
        if (match) {
            const compareValue = match[2].trim().replace(/['"]/g, '');
            return {
                column: match[1],
                operator: pattern.operator,
                compareValue: compareValue,
                valueType: isNaN(compareValue) ? 'text' : 'number'
            };
        }
    }

    return { column: 'value', operator: 'greater_than', compareValue: '', valueType: 'number' };
};

/**
 * Build condition string from structured format
 */
const buildCondition = (column, operator, compareValue, valueType) => {
    const col = column || 'value';
    const val = valueType === 'text' ? `'${compareValue}'` : compareValue;

    switch (operator) {
        case 'equals':
            return `${col} == ${val}`;
        case 'not_equals':
            return `${col} != ${val}`;
        case 'greater_than':
            return `${col} > ${val}`;
        case 'greater_than_or_equal':
            return `${col} >= ${val}`;
        case 'less_than':
            return `${col} < ${val}`;
        case 'less_than_or_equal':
            return `${col} <= ${val}`;
        case 'contains':
            return `${col}.includes(${val})`;
        case 'not_contains':
            return `!${col}.includes(${val})`;
        case 'starts_with':
            return `${col}.startsWith(${val})`;
        case 'ends_with':
            return `${col}.endsWith(${val})`;
        case 'is_empty':
            return `${col} == '' || ${col} == null`;
        case 'is_not_empty':
            return `${col} != '' && ${col} != null`;
        default:
            return `${col} == ${val}`;
    }
};

const ConditionBuilder = ({
    value,
    onChange,
    availableColumns = ['value', 'Name', 'Date', 'Amount', 'Status']
}) => {
    const parsed = parseCondition(value);
    const [column, setColumn] = useState(parsed.column);
    const [operator, setOperator] = useState(parsed.operator);
    const [compareValue, setCompareValue] = useState(parsed.compareValue);
    const [valueType, setValueType] = useState(parsed.valueType);

    const handleChange = (newColumn, newOperator, newCompareValue, newValueType) => {
        const condition = buildCondition(newColumn, newOperator, newCompareValue, newValueType);
        onChange(condition);
    };

    const needsValue = !['is_empty', 'is_not_empty'].includes(operator);

    return (
        <div className="condition-builder">
            <div className="condition-builder-row">
                <div className="condition-field">
                    <label>When</label>
                    <select
                        className="condition-select"
                        value={column}
                        onChange={(e) => {
                            setColumn(e.target.value);
                            handleChange(e.target.value, operator, compareValue, valueType);
                        }}
                    >
                        {availableColumns.map(col => (
                            <option key={col} value={col}>{col}</option>
                        ))}
                    </select>
                </div>

                <div className="condition-field">
                    <label>Condition</label>
                    <select
                        className="condition-select"
                        value={operator}
                        onChange={(e) => {
                            setOperator(e.target.value);
                            handleChange(column, e.target.value, compareValue, valueType);
                        }}
                    >
                        {OPERATORS.map(op => (
                            <option key={op.value} value={op.value}>{op.label}</option>
                        ))}
                    </select>
                </div>

                {needsValue && (
                    <>
                        <div className="condition-field condition-value-type">
                            <label>Type</label>
                            <select
                                className="condition-select"
                                value={valueType}
                                onChange={(e) => {
                                    setValueType(e.target.value);
                                    handleChange(column, operator, compareValue, e.target.value);
                                }}
                            >
                                {VALUE_TYPES.map(type => (
                                    <option key={type.value} value={type.value}>{type.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="condition-field condition-value-input">
                            <label>Value</label>
                            <input
                                type={valueType === 'number' ? 'number' : valueType === 'date' ? 'date' : 'text'}
                                className="condition-input"
                                value={compareValue}
                                onChange={(e) => {
                                    setCompareValue(e.target.value);
                                    handleChange(column, operator, e.target.value, valueType);
                                }}
                                placeholder={valueType === 'number' ? '100' : 'Enter value...'}
                            />
                        </div>
                    </>
                )}
            </div>

            <div className="condition-preview">
                <span className="condition-preview-label">Generated condition:</span>
                <code className="condition-preview-code">
                    {buildCondition(column, operator, compareValue, valueType)}
                </code>
            </div>
        </div>
    );
};

export default ConditionBuilder;
